'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { nav } from '@/lib/content';
import type { NavItem } from '@/lib/content';
import { business } from '@/lib/brand';

// Build a stable identity that's unique even when two nav items share an
// href (Shop All and Shop by Note both link to /shop, so href alone
// collides as a React key).
const itemKey = (i: { label: string; href: string }) => `${i.label}::${i.href}`;

export function MobileMenu() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  function toggleExpand(key: string) {
    setExpanded((cur) => (cur === key ? null : key));
  }

  function close() {
    setOpen(false);
    setExpanded(null);
  }

  const drawer = open && (
    <div className="fixed inset-0 z-[100] md:hidden">
      {/* Backdrop */}
      <button
        onClick={close}
        aria-label="Close menu"
        className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
      />
      {/* Panel — full-height slide-in from the right */}
      <div className="absolute top-0 right-0 bottom-0 w-[92vw] max-w-md bg-bone shadow-2xl flex flex-col animate-[slide-in-right_220ms_ease-out]">
        <div className="flex items-center justify-between px-6 h-16 border-b border-ink/10 shrink-0">
          <span className="font-display tracking-[0.1em] font-semibold text-base text-ink">{business.shortName}</span>
          <button
            onClick={close}
            aria-label="Close menu"
            className="text-ink/70 hover:text-ink"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        </div>
        <nav className="flex-1 px-6 py-6 overflow-y-auto">
          <ul className="border-t border-ink/10">
            {nav.primary.map((item) => {
              const k = itemKey(item);
              return (
                <PrimaryItem
                  key={k}
                  item={item}
                  expanded={expanded === k}
                  onToggle={() => toggleExpand(k)}
                  onNavigate={close}
                />
              );
            })}
            {nav.secondary.map((item) => (
              <li key={itemKey(item)} className="border-b border-ink/10">
                <Link
                  href={item.href}
                  onClick={close}
                  className="block py-4 text-sm font-display tracking-[0.1em] font-semibold text-ink hover:text-gold transition-colors"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Quick link: favorites on mobile */}
          <div className="mt-8">
            <Link
              href="/wishlist"
              onClick={close}
              className="block border border-ink/20 px-4 py-3 text-xs uppercase tracking-[0.12em] font-medium text-ink/75 hover:text-ink hover:border-ink transition-colors text-center"
            >
              Favorites
            </Link>
          </div>

          <div className="mt-10">
            <p className="text-xs uppercase tracking-[0.14em] text-ink/65 mb-4 font-medium">Information</p>
            <ul className="space-y-3">
              {nav.legal.map((item) => (
                <li key={itemKey(item)}>
                  <Link
                    href={item.href}
                    onClick={close}
                    className="text-sm text-ink/70 hover:text-ink transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 pt-8 border-t border-ink/10">
            <p className="text-xs uppercase tracking-[0.14em] text-ink/65 mb-4 font-medium">Contact</p>
            <a href={`mailto:${business.contactEmail}`} className="block text-sm text-ink/70 hover:text-ink mb-2 break-all">{business.contactEmail}</a>
            <a href={`tel:${business.contactPhone.replace(/[^\d+]/g, '')}`} className="block text-sm text-ink/70 hover:text-ink font-numerals">{business.contactPhone}</a>
          </div>
        </nav>
      </div>
    </div>
  );

  return (
    <>
      {/* Hamburger trigger — shown on mobile only */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        aria-expanded={open}
        className="md:hidden text-ink/70 hover:text-ink"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <line x1="3" y1="7"  x2="21" y2="7" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="17" x2="21" y2="17" />
        </svg>
      </button>

      {/* Portal the drawer to document.body so it escapes any ancestor
          containing-block created by the header's backdrop-filter. */}
      {mounted && drawer && createPortal(drawer, document.body)}
    </>
  );
}

function PrimaryItem({
  item,
  expanded,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  // Mobile collapses mega-menus down to grouped sections; simple children
  // dropdowns stay flat. Plain links render without an expander.
  const sections = item.mega ?? null;
  const flatChildren = item.children ?? null;
  const hasChildren = !!(sections?.length || flatChildren?.length);

  if (!hasChildren) {
    return (
      <li className="border-b border-ink/10">
        <Link
          href={item.href}
          onClick={onNavigate}
          className="block py-4 text-sm font-display tracking-[0.1em] font-semibold text-ink hover:text-gold transition-colors"
        >
          {item.label}
        </Link>
      </li>
    );
  }

  return (
    <li className="border-b border-ink/10">
      <div className="flex items-center justify-between">
        <Link
          href={item.href}
          onClick={onNavigate}
          className="block py-4 text-sm font-display tracking-[0.1em] font-semibold text-ink hover:text-gold transition-colors flex-1"
        >
          {item.label}
        </Link>
        <button
          onClick={onToggle}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label}`}
          aria-expanded={expanded}
          className="px-3 py-2 text-muted-strong hover:text-ink transition-colors"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
          >
            <path d="M3 5.5l4 4 4-4" />
          </svg>
        </button>
      </div>

      {expanded && sections && (
        <div className="pb-5 pl-1 space-y-5">
          {sections.map((section) => (
            <div key={section.title}>
              {/* Section header: small image/gradient thumb + title — ties
                  mobile to the desktop mega-panel visually. */}
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`relative w-14 h-14 shrink-0 overflow-hidden rounded-sm bg-gradient-to-br ${
                    section.image.gradient ?? 'from-bone to-ink/[0.04]'
                  }`}
                  aria-hidden="true"
                >
                  {section.image.src ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={section.image.src}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : section.image.label ? (
                    <span className="absolute inset-0 flex items-center justify-center font-display italic text-xs text-ink/35 select-none">
                      {section.image.label}
                    </span>
                  ) : null}
                </div>
                <p className="text-xs uppercase tracking-[0.14em] text-ink/65 font-medium">
                  {section.title}
                </p>
              </div>
              <ul className="space-y-1 pl-[68px] -mt-1">
                {section.links.map((link) => (
                  <li key={link.href + link.label}>
                    <Link
                      href={link.href}
                      onClick={onNavigate}
                      className="block py-2 text-xs uppercase tracking-[0.12em] text-ink/70 hover:text-ink transition-colors font-medium"
                    >
                      <span className="inline-flex items-center gap-2">
                        {link.label}
                        {link.badge === 'new' && (
                          <span className="text-[8px] tracking-[0.1em] font-bold text-rose">new</span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {expanded && flatChildren && !sections && (
        <ul className="pb-4 pl-1 space-y-1">
          {flatChildren.map((child) => (
            <li key={child.href + child.label}>
              <Link
                href={child.href}
                onClick={onNavigate}
                className="block py-2.5 text-xs uppercase tracking-[0.12em] text-ink/65 hover:text-ink transition-colors font-medium"
              >
                {child.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
